import { getAuthState, getFeatureFlag } from "@sparky/framework";

export const RED_TEAMING = "red-teaming" as const;
export const MODEL_SCANNING = "model-scanning" as const;

export type PRODUCT_TYPE = typeof RED_TEAMING | typeof MODEL_SCANNING;

const MFE_KEYS = {
	[RED_TEAMING]: "airt.red-teaming",
	[MODEL_SCANNING]: "airt.model-scanning",
} as const;

const FF_KEYS = {
	[RED_TEAMING]: "vnr.airt.red_teaming",
	[MODEL_SCANNING]: "vnr.airt.model_scanning",
} as const;

export const INSTANCE_KEYS = {
	[RED_TEAMING]: "ai_rt",
	[MODEL_SCANNING]: "ai_ms",
} as const;

const SUB_TYPE_KEYS = {
	[RED_TEAMING]: "RT",
	[MODEL_SCANNING]: "MS",
} as const;

export type DeploymentProfile = {
	dAuthCode: string;
	deploymentProfileId: string;
	maxThreatSimulations: number;
	license_expiration: string;
	profileName: string;
	subType: "RT" | "MS";
};

export type DPActivationStatus = Record<string, { active: boolean }>;
// { "deployment_123": { active: true }, "deployment_234": { active: false } }

const OPTIMISTIC_CACHE = {} as Record<PRODUCT_TYPE, DPActivationStatus>;

export const getDPList = (type: PRODUCT_TYPE): DeploymentProfile[] => {
	const subType = SUB_TYPE_KEYS[type];
	const instance = getAuthState().instances.get(INSTANCE_KEYS[type]);
	return (
		instance?.extra?.deployment_profiles.filter(
			(dp: DeploymentProfile) => dp.subType === subType,
		) ?? []
	);
};

export function getDPActivationStatus(type: PRODUCT_TYPE): DPActivationStatus {
	const mfeKey = MFE_KEYS[type];

	// Flow 1 - if we have optimistic cache, use it. It will be set up by onboarding flow once activation successful since we can't refetch updated MFE property value.
	if (OPTIMISTIC_CACHE[type]) {
		return OPTIMISTIC_CACHE[type];
	}

	// Flow 2 - if we are in fake mode, use the localStorage
	if (
		localStorage.getItem("airt.fakeOnboarding") === "true" &&
		localStorage.getItem(mfeKey) &&
		localStorage.getItem(mfeKey) !== "true"
	) {
		return JSON.parse(localStorage.getItem(mfeKey)!);
	}

	// Flow 3 - if we are not in fake mode, use the properties
	const mfePropertyValue: DPActivationStatus = getAuthState().properties?.[mfeKey]?.value ?? {};
	return mfePropertyValue;
}

export function isFeatureAvailable(type: PRODUCT_TYPE) {
	return getFeatureFlag(FF_KEYS[type]);
}

// Will change in future once we know how to deal with multiple deployment profiles
export function isActivated(type: PRODUCT_TYPE) {
	const statusPerDP = getDPActivationStatus(type);

	return Object.values(statusPerDP).some((item) => item.active);
}

export const isAllowed = (type: PRODUCT_TYPE) => {
	const ff = isFeatureAvailable(type);
	const activated = isActivated(type);

	return ff && activated;
};

export const updateOptimisticCache = (type: PRODUCT_TYPE, dpID: string) => {
	const oldDPActivationStatus = getDPActivationStatus(type);

	OPTIMISTIC_CACHE[type] = {
		...oldDPActivationStatus,
		[dpID]: { active: true },
	};
};

export const updateLocalStorageActivationStatus = (type: PRODUCT_TYPE, dpID: string) => {
	const oldStatusPerDP = getDPActivationStatus(type);
	const mfeKey = MFE_KEYS[type];

	localStorage.setItem(mfeKey, JSON.stringify({ ...oldStatusPerDP, [dpID]: { active: true } }));
};
